import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Pencil } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import FilterRulesEditor from './FilterRulesEditor';
import { previewSpanVariables } from 'features/evals3/api/evals3Api';

/**
 * Span variables: a run's spans in a judge prompt, chosen the way a person
 * says it — "the last tool_selection", "every search inside the planner",
 * "the order things ran in" — and read as {{spans.NAME}}.
 *
 * Four choices, each explicit: which spans (the app's filter chips over a
 * span's properties, and optionally only inside another span), which of
 * them, what to read, and what happens when none match. The preview runs it
 * over recent traffic before anything is saved: how many spans it matches
 * per run (so "last" never silently hides four others) and the exact text
 * the judge would get. The server's spanVariables.js is the contract.
 */

export const PICKS = [
  { value: 'all', label: 'All of them' },
  { value: 'first', label: 'The first' },
  { value: 'last', label: 'The last' },
  { value: 'nth', label: 'The Nth' },
  { value: 'first_n', label: 'The first N' },
  { value: 'last_n', label: 'The last N' },
];
export const READS = [
  { value: 'input_output', label: 'Input and output' },
  { value: 'input', label: 'Input (a tool’s arguments)' },
  { value: 'output', label: 'Output' },
  { value: 'error', label: 'Error' },
  { value: 'metadata', label: 'A metadata key' },
  { value: 'duration_ms', label: 'Duration' },
  { value: 'tokens', label: 'Tokens' },
  { value: 'names', label: 'Names, in order' },
  { value: 'structure', label: 'Outline of the run' },
  { value: 'count', label: 'How many' },
  { value: 'exists', label: 'Whether any' },
];
export const IF_NONE = [
  { value: 'skip', label: 'Skip the run (not scored)' },
  { value: 'tell', label: 'Tell the judge none matched' },
  { value: 'fail', label: 'Fail the step' },
  { value: 'default', label: 'Use this text' },
];
const VALUES_OF = (s) => s.read === 'count' || s.read === 'exists';

const NAME_RE = /^[a-z][a-z0-9_]*$/;
const label = (list, v) => (list.find((o) => o.value === v) || list[0]).label;

/** A new variable's name: calls, calls_2, … */
export function freshName(existing, base = 'spans') {
  const taken = new Set(existing);
  if (!taken.has(base)) return base;
  for (let i = 2; ; i += 1) if (!taken.has(`${base}_${i}`)) return `${base}_${i}`;
}

/** One line a person reads: "The last · kind is tool · input and output". */
export function summaryOf(spec) {
  const conds = ((spec.where && spec.where.all) || []).map((r) => {
    const field = String(r.path || '').replace(/^span\./, '');
    const v = Array.isArray(r.value) ? r.value.join(' or ') : r.value;
    return v === undefined ? field : `${field} ${r.op === 'eq' || r.op === 'in' ? 'is' : r.op} ${v}`;
  });
  const which = spec.pick === 'nth' || spec.pick === 'first_n' || spec.pick === 'last_n'
    ? label(PICKS, spec.pick).replace('N', String(spec.n ?? 'N')) : label(PICKS, spec.pick || 'all');
  const parts = [VALUES_OF(spec) ? null : which, conds.length ? conds.join(', ') : 'every span'];
  if (spec.within) parts.push(spec.within.direct ? 'directly under a match' : 'inside a match');
  parts.push(spec.read === 'metadata' ? `metadata.${spec.metadata_key || '…'}` : label(READS, spec.read || 'input_output').toLowerCase());
  return parts.filter(Boolean).join(' · ');
}

/** A spec as the server expects it: no empty filters, no stray fields. */
export function cleanSpec(s) {
  const out = { name: s.name, pick: s.pick || 'all', read: s.read || 'input_output', if_none: s.if_none || 'skip' };
  if (s.where && Array.isArray(s.where.all) && s.where.all.length) out.where = s.where;
  else if (s.where && Array.isArray(s.where.any) && s.where.any.length) out.where = s.where;
  if (s.within && s.within.where && ((s.within.where.all || s.within.where.any || []).length)) out.within = { where: s.within.where, ...(s.within.direct ? { direct: true } : {}) };
  if (['nth', 'first_n', 'last_n'].includes(out.pick)) out.n = Number(s.n) || 1;
  if (out.read === 'metadata') out.metadata_key = s.metadata_key || '';
  if (out.if_none === 'default') out.default = s.default ?? '';
  for (const k of ['max_items', 'field_chars', 'total_chars']) if (s[k]) out[k] = Number(s[k]);
  return out;
}

/**
 * The step's variables, each a row: its {{reference}}, what it reads, edit and remove.
 * onChange(nextVars, insertRef): insertRef is the reference a new variable puts in the prompt.
 */
export function SpanVariablesSection({ vars = [], onChange, onInsert, scopes, target, teamId, agentName, readOnly }) {
  const [editing, setEditing] = useState(null); // { index, spec } | null
  const names = vars.map((v) => v.name);
  const open = (index) => setEditing({ index, spec: index === -1 ? { name: freshName(names), pick: 'all', read: 'input_output', if_none: 'skip' } : vars[index] });
  // One change for both: a new variable and its reference in the prompt. Two
  // updates in a row would each start from the config before the other.
  const save = (spec) => {
    const next = [...vars];
    if (editing.index === -1) next.push(spec); else next[editing.index] = spec;
    onChange(next, editing.index === -1 ? `{{spans.${spec.name}}}` : null);
    setEditing(null);
  };
  return (
    <div>
      {vars.map((v, i) => (
        <div key={v.name} className="mb-1.5 flex items-center gap-2 rounded-md border border-[color:var(--border-default)] px-2 py-1.5">
          <button type="button" className="eval3-var-pill flex-none font-mono text-[11.5px]" title="Drag into the prompt, or click to insert" draggable={!readOnly}
            onDragStart={(e) => { e.dataTransfer.setData('text/plain', `{{spans.${v.name}}}`); e.dataTransfer.effectAllowed = 'copy'; }}
            onClick={() => onInsert && onInsert(`{{spans.${v.name}}}`)} disabled={readOnly}>
            {`spans.${v.name}`}
          </button>
          <span className="min-w-0 flex-1 truncate text-[12px] text-[color:var(--text-secondary)]" title={summaryOf(v)}>{summaryOf(v)}</span>
          {!readOnly && (
            <>
              <button type="button" aria-label={`Edit ${v.name}`} onClick={() => open(i)} className="flex-none text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><Pencil size={13} /></button>
              <button type="button" aria-label={`Remove ${v.name}`} onClick={() => onChange(vars.filter((_, k) => k !== i))} className="flex-none text-[color:var(--text-muted)] hover:text-danger"><X size={13} /></button>
            </>
          )}
        </div>
      ))}
      {!readOnly && (
        <button type="button" onClick={() => open(-1)}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]">
          <Plus size={13} /> Add spans
        </button>
      )}
      {editing && (
        <SpanVariableDialog initial={editing.spec} taken={names.filter((_, i) => i !== editing.index)}
          scopes={scopes} target={target} teamId={teamId} agentName={agentName}
          onSave={save} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function Row({ label: text, children }) {
  return (
    <div className="mb-3">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-secondary)]">{text}</div>
      {children}
    </div>
  );
}

const BUCKET_LABEL = { 0: 'none', 1: 'one', '2-5': '2–5', '6+': '6 or more' };

function Preview({ result, error, loading, spec }) {
  if (error) return <div className="text-[12px] text-danger">{error}</div>;
  if (!result) return <div className="text-[12px] text-[color:var(--text-muted)]">{loading ? 'Reading recent runs…' : ''}</div>;
  const v = result.variables[0];
  if (!result.sampled) return <div className="text-[12px] text-[color:var(--text-muted)]">No runs yet to try it on.</div>;
  const hist = v.histogram;
  return (
    <div className="text-[12px]">
      <div className="mb-1 text-[color:var(--text-secondary)]">Spans matched per run, latest {result.sampled} runs</div>
      <div className="mb-2 grid grid-cols-4 gap-1">
        {Object.entries(hist).map(([b, n]) => (
          <div key={b} className={`rounded-md border px-2 py-1 ${b === '0' && n && spec.if_none === 'skip' ? 'border-warning' : 'border-[color:var(--border-default)]'}`}>
            <div className="text-[10.5px] text-[color:var(--text-muted)]">{BUCKET_LABEL[b]}</div>
            <div className="tabular-nums text-[13px] text-[color:var(--text-primary)]">{n}</div>
          </div>
        ))}
      </div>
      {v.hidden_by_pick > 0 && (
        <div className="mb-2 text-warning">In {v.hidden_by_pick} of {result.sampled} runs more spans matched than this reads.</div>
      )}
      {v.empty > 0 && spec.if_none === 'skip' && (
        <div className="mb-2 text-[color:var(--text-secondary)]">{v.empty} of {result.sampled} runs would not be scored.</div>
      )}
      {v.example && (
        <>
          <div className="mb-1 text-[color:var(--text-secondary)]">What the judge gets, on the run with the most matches</div>
          <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap break-words rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-2 font-mono text-[11px] leading-[1.45] text-[color:var(--text-primary)]">
            {v.example.text || '(empty)'}
          </pre>
        </>
      )}
    </div>
  );
}

/** Pick one span name the agent emits: adds "name is X" to the filter. */
function SpanNames({ names, onPick }) {
  if (!names || !names.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {names.slice(0, 12).map((n) => (
        <button key={`${n.kind}:${n.name}`} type="button" onClick={() => onPick(n)} title={`${n.kind || 'span'} · in ${n.runs} runs`}
          className="rounded-md border border-[color:var(--border-default)] px-1.5 py-0.5 font-mono text-[11px] text-[color:var(--text-secondary)] hover:bg-[color:var(--surface-hover)]">
          {n.name}
        </button>
      ))}
    </div>
  );
}

export function SpanVariableDialog({ initial, taken = [], scopes, target, teamId, agentName, onSave, onClose }) {
  const [spec, setSpec] = useState(() => ({ ...initial }));
  const [inside, setInside] = useState(Boolean(initial.within));
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (patch) => setSpec((s) => ({ ...s, ...patch }));
  const spanScopes = useMemo(() => ({ span: (scopes && scopes.span) || [] }), [scopes]);

  const nameProblem = !NAME_RE.test(spec.name || '') ? 'lower_snake_case, starting with a letter'
    : taken.includes(spec.name) ? 'another variable has this name' : null;
  const clean = useMemo(() => cleanSpec({ ...spec, within: inside ? spec.within : undefined }), [spec, inside]);
  const key = JSON.stringify(clean);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!teamId || nameProblem) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      previewSpanVariables(teamId, { target: target === 'conversation' ? 'conversation' : 'run', agent_name: agentName || undefined, span_vars: [clean], sample: 25 })
        .then((r) => { if (!cancelled) { setResult(r); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e?.response?.data?.message || e?.response?.data?.error || e.message); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, teamId, agentName, target]);

  const addName = (n) => {
    const all = [...((spec.where && spec.where.all) || []).filter((r) => r.path !== 'span.name'), { path: 'span.name', op: 'eq', value: n.name }];
    set({ where: { all } });
  };
  const needsN = ['nth', 'first_n', 'last_n'].includes(spec.pick);
  const canSave = !nameProblem && !(spec.read === 'metadata' && !String(spec.metadata_key || '').trim());

  const el = (
    <div className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[6vh]"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 40%, transparent)' }}
      role="dialog" aria-modal="true" aria-labelledby="span-var-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[86vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-2xl">
        <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-5 py-3">
          <h2 id="span-var-title" className="text-[14px] font-semibold text-[color:var(--text-primary)]">Spans</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><X size={16} /></button>
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-auto px-5 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div>
            <Row label="Name">
              <div className="flex items-center gap-1 font-mono text-[12.5px]">
                <span className="text-[color:var(--text-muted)]">{'{{spans.'}</span>
                <input aria-label="Variable name" value={spec.name} onChange={(e) => set({ name: e.target.value.trim() })} className="input h-7 w-48 font-mono text-[12.5px]" />
                <span className="text-[color:var(--text-muted)]">{'}}'}</span>
              </div>
              {nameProblem && <div className="mt-1 text-[11px] text-danger">{nameProblem}</div>}
            </Row>
            <Row label="Which spans">
              <FilterRulesEditor value={spec.where || { all: [] }} target="span" scopes={spanScopes} teamId={teamId}
                onChange={(where) => set({ where })} />
              <SpanNames names={result?.span_names} onPick={addName} />
              <label className="mt-2 flex items-center gap-1.5 text-[12px] text-[color:var(--text-secondary)]">
                <input type="checkbox" checked={inside} onChange={(e) => { setInside(e.target.checked); if (e.target.checked && !spec.within) set({ within: { where: { all: [] } } }); }} />
                Only inside another span
              </label>
              {inside && (
                <div className="mt-1.5 border-l-2 border-[color:var(--border-default)] pl-3">
                  <FilterRulesEditor value={(spec.within && spec.within.where) || { all: [] }} target="span" scopes={spanScopes} teamId={teamId}
                    onChange={(where) => set({ within: { ...(spec.within || {}), where } })} />
                  <label className="mt-1.5 flex items-center gap-1.5 text-[12px] text-[color:var(--text-secondary)]">
                    <input type="checkbox" checked={Boolean(spec.within && spec.within.direct)} onChange={(e) => set({ within: { ...(spec.within || { where: { all: [] } }), direct: e.target.checked } })} />
                    Directly under it
                  </label>
                </div>
              )}
            </Row>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Read">
                <BrandDropdown ariaLabel="Read" value={spec.read || 'input_output'} onChange={(read) => set({ read })} options={READS} />
                {spec.read === 'metadata' && (
                  <input aria-label="Metadata key" placeholder="key" value={spec.metadata_key || ''} onChange={(e) => set({ metadata_key: e.target.value })} className="input mt-1.5 h-7 w-full font-mono text-[12px]" />
                )}
              </Row>
              {!VALUES_OF(spec) && (
                <Row label="Which of them">
                  <div className="flex items-center gap-1.5">
                    <div className="min-w-0 flex-1"><BrandDropdown ariaLabel="Which of them" value={spec.pick || 'all'} onChange={(pick) => set({ pick })} options={PICKS} /></div>
                    {needsN && (
                      <input aria-label="N" type="number" value={spec.n ?? 1} onChange={(e) => set({ n: e.target.value === '' ? '' : Number(e.target.value) })}
                        className="input h-8 w-16 tabular-nums" title={spec.pick === 'nth' ? 'from the start; negative counts from the end' : undefined} />
                    )}
                  </div>
                </Row>
              )}
            </div>
            {!VALUES_OF(spec) && (
              <Row label="If none match">
                <BrandDropdown ariaLabel="If none match" value={spec.if_none || 'skip'} onChange={(if_none) => set({ if_none })} options={IF_NONE} />
                {spec.if_none === 'default' && (
                  <input aria-label="Text to use" value={spec.default ?? ''} onChange={(e) => set({ default: e.target.value })} className="input mt-1.5 h-7 w-full text-[12px]" />
                )}
              </Row>
            )}
            {!VALUES_OF(spec) && !['names', 'structure'].includes(spec.read) && (
              <details className="text-[12px] text-[color:var(--text-secondary)]">
                <summary className="cursor-pointer text-[11px]">Limits</summary>
                <div className="mt-1.5 grid grid-cols-3 gap-2">
                  {[['max_items', 'Spans', 20], ['field_chars', 'Characters per field', 2000], ['total_chars', 'Characters in all', 24000]].map(([k, l, d]) => (
                    <label key={k} className="block">
                      <div className="mb-0.5 text-[10.5px] text-[color:var(--text-muted)]">{l}</div>
                      <input type="number" aria-label={l} placeholder={String(d)} value={spec[k] ?? ''} onChange={(e) => set({ [k]: e.target.value === '' ? undefined : Number(e.target.value) })} className="input h-7 w-full tabular-nums" />
                    </label>
                  ))}
                </div>
              </details>
            )}
          </div>
          <div className="min-w-0">
            <Preview result={result} error={error} loading={loading} spec={clean} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[color:var(--border-default)] px-5 py-3">
          <button type="button" onClick={onClose} className="h-8 rounded-md px-3 text-[12.5px] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]">Cancel</button>
          <button type="button" disabled={!canSave} onClick={() => onSave(clean)} className="h-8 rounded-md bg-brand px-3 text-[12.5px] font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-50">Save</button>
        </div>
      </div>
    </div>
  );
  return createPortal(el, document.body);
}
